import {NamedValue, Paths} from "@2d-front-monorepo/core";
import {useCallback, useMemo} from "react";
import {IMvvmContextState} from "./MvvmContextState";
import {unwrapMvvmContext} from "./useMvvmTracking";
import {useSelectedItem} from "./useSelectedItem";
import {useSelectedItems} from "./useSelectedItems";

export interface MvvmSelectionResult {
	/** Currently selected entries, expressed as index strings the option controls render against. */
	selectedValues: string[];
	/** Translates the control's index-based change event back into model value(s) and writes them. */
	onChangeInternal: (selectedIndexes: number[]) => void;
}

/**
 * Binds a single-select option control to its model path: reads the current value, resolves it to
 * the matching entry index, and writes the picked entry's value (or `undefined` when cleared) back.
 * Shared by MvvmOptionList and MvvmOptionGroup.
 */
export function useMvvmSingleSelection<T extends object, TValue>(
	context: IMvvmContextState<T>,
	property: Paths<T>,
	items: NamedValue<TValue>[],
	onChange?: (value: TValue | undefined) => void
): MvvmSelectionResult {
	// Raw context: the option control subscribes to its own field; see unwrapMvvmContext.
	context = unwrapMvvmContext(context);
	const value = context.getValue<TValue>(property);
	const selectedItem = useSelectedItem(items, value);
	const selectedValues = useMemo(() => [selectedItem], [selectedItem]);

	const onChangeInternal = useCallback(function (selectedIndexes: number[]): void {
		const selected = selectedIndexes.length === 0 ? undefined : items.at(selectedIndexes[0])?.value;
		onChange?.(selected);
		context.setValue(property, selected);
	}, [context, property, items, onChange]);

	return {selectedValues, onChangeInternal};
}

/**
 * Binds a multi-select option control to its model path: reads the current value array, resolves it
 * to the matching entry indexes, and writes the picked entries' values back.
 * Shared by MvvmMultiOptionList and MvvmMultiOptionGroup.
 */
export function useMvvmMultiSelection<T extends object, TValue>(
	context: IMvvmContextState<T>,
	property: Paths<T>,
	items: NamedValue<TValue>[],
	onChange?: (value: TValue[] | undefined) => void
): MvvmSelectionResult {
	// Raw context: the option control subscribes to its own field; see unwrapMvvmContext.
	context = unwrapMvvmContext(context);
	const values = context.getValue<TValue[]>(property);
	const selectedValues = useSelectedItems(items, values);

	const onChangeInternal = useCallback(function (selectedIndexes: number[]): void {
		const selected = selectedIndexes
			.map(x => items.at(x)?.value)
			.filter((x): x is TValue => x != null);

		onChange?.(selected);
		context.setValue(property, selected);
	}, [context, property, items, onChange]);

	return {selectedValues, onChangeInternal};
}
